/**
 * Workspace policy: require the installed MissionReady app for live GPS.
 *
 * Additive and honest. A phone browser stops sending positions the moment the
 * app isn't the visible tab, so an admin can require the installed app for
 * on-duty live location. When the policy is on and the guard is in a browser,
 * the app tells them to install it instead of pretending the browser will keep
 * reporting. Clock in/out, geofences, patrols, RBAC and the stored location
 * pipeline are unchanged.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hasNativeDutyLocation, isNativeShell } from "@/lib/native-duty-location";

/** Existing Android listing for the app (same package identity). */
export const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=com.missionready.security";

/** True when this workspace requires the installed app for live GPS. */
export async function fetchRequireNativeLiveGps(orgId: string): Promise<boolean> {
  if (!orgId) return false;
  const { data } = await supabase
    .from("org_feature_flags")
    .select("require_native_live_gps")
    .eq("organization_id", orgId)
    .maybeSingle();
  return !!(data as { require_native_live_gps?: boolean } | null)?.require_native_live_gps;
}

export function useRequireNativeLiveGps(orgId: string | null | undefined) {
  const [required, setRequired] = useState(false);
  useEffect(() => {
    let live = true;
    if (!orgId) { setRequired(false); return; }
    void fetchRequireNativeLiveGps(orgId).then((v) => { if (live) setRequired(v); });
    return () => { live = false; };
  }, [orgId]);
  return required;
}

/** True when the policy applies to this device: required, but not the app. */
export function nativeGpsRequiredHere(required: boolean): boolean {
  return required && !(isNativeShell() && hasNativeDutyLocation());
}
